import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { publicApi } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";
import Seo from "@/components/seo/Seo";
import { NORMA_CONTENT } from "@/data/normaContent";
import { serviceSlugFor } from "@/lib/serviceLinks";

export default function NormaDetalle() {
  const { slug } = useParams();
  const dataSlug = slug === "iso-27001" ? "isoiec-27001" : slug;
  const [norma, setNorma] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    publicApi.normaDetail(dataSlug).then(setNorma)
      .catch(() => setNotFound(true));
  }, [dataSlug]);

  if (notFound) return <div className="pt-32 text-center">Norma no encontrada. <Link to="/#normas" className="underline">Ver todas</Link></div>;
  if (!norma) return <div className="pt-32 text-center">Cargando...</div>;

  const content = NORMA_CONTENT[dataSlug];
  const description = content?.seoDescription || norma.resumen;
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `Consultoría ${norma.codigo}`,
      description,
      url: `https://www.kbconsultoria.com/normas/${slug}`,
      provider: { "@type": "Organization", "@id": "https://www.kbconsultoria.com/#organization", name: "KB Consultoría", url: "https://www.kbconsultoria.com/" },
      areaServed: ["Costa Rica", "Centroamérica"],
      serviceType: ["Diagnóstico", "Implementación", "Auditoría interna", "Preparación para certificación"],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: "https://www.kbconsultoria.com/" },
        { "@type": "ListItem", position: 2, name: "Normas", item: "https://www.kbconsultoria.com/#normas" },
        { "@type": "ListItem", position: 3, name: norma.codigo, item: `https://www.kbconsultoria.com/normas/${slug}` },
      ],
    },
  ];
  if (content?.faq?.length) {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faq.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
      })),
    });
  }

  return (
    <main className="pt-[72px]" data-testid="norma-detail-page">
      <Seo title={content?.seoTitle || `${norma.codigo}: ${norma.titulo}`} description={description} path={`/normas/${slug}`} structuredData={schema} />
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-8">
        <Link to="/#normas" className="inline-flex items-center gap-2 text-xs text-[#4A4A4A] hover:text-[#1A1A1A] eyebrow">
          <ArrowLeft className="w-4 h-4" /> Todas las normas
        </Link>
      </div>

      <header className="border-b border-[#1A1A1A]/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="eyebrow mb-6">— Norma</div>
          <h1 className="display-1 max-w-5xl">{content?.seoTitle || norma.codigo}</h1>
          <p className="mt-6 font-serif text-2xl md:text-3xl italic text-[#1B3B2B]">{norma.titulo}</p>
        </div>
      </header>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <Reveal className="col-span-12 md:col-span-7">
            <div className="eyebrow mb-3">Resumen</div>
            <p className="font-serif text-2xl md:text-3xl leading-tight">{content?.introduccion || norma.resumen}</p>

            {content?.beneficios && (
              <div className="mt-16">
                <h2 className="font-serif text-3xl md:text-4xl">Beneficios para su organización</h2>
                <ul className="mt-6 space-y-3">
                  {content.beneficios.map((beneficio) => (
                    <li key={beneficio} className="flex items-start gap-3 py-3 border-b border-[#1A1A1A]/10">
                      <Check className="w-4 h-4 mt-1 text-[#1B3B2B] shrink-0" />
                      <span>{beneficio}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {content?.aplica && (
              <div className="mt-16">
                <h2 className="font-serif text-3xl md:text-4xl">¿Para quién aplica?</h2>
                <p className="mt-5 text-lg leading-relaxed text-[#4A4A4A]">{content.aplica}</p>
              </div>
            )}

            {content?.etapas && (
              <div className="mt-16">
                <h2 className="font-serif text-3xl md:text-4xl">Cómo desarrollamos el proyecto</h2>
                <ol className="mt-6 grid sm:grid-cols-2 gap-3">
                  {content.etapas.map((etapa, index) => (
                    <li key={etapa} className="border border-[#1A1A1A]/10 p-5">
                      <span className="eyebrow text-[#1B3B2B]">{String(index + 1).padStart(2, "0")}</span>
                      <div className="mt-2">{etapa}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {content?.faq && (
              <section className="mt-16" aria-labelledby="norma-faq-title">
                <h2 id="norma-faq-title" className="font-serif text-3xl md:text-4xl">Preguntas frecuentes</h2>
                <div className="mt-6 divide-y divide-[#1A1A1A]/10 border-y border-[#1A1A1A]/10">
                  {content.faq.map(([question, answer]) => (
                    <article key={question} className="py-6">
                      <h3 className="font-serif text-2xl">{question}</h3>
                      <p className="mt-3 leading-relaxed text-[#4A4A4A]">{answer}</p>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {norma.servicios_relacionados && (
              <div className="mt-16">
                <div className="eyebrow mb-6">Servicios relacionados</div>
                <ul className="space-y-3">
                  {norma.servicios_relacionados.map((s, i) => {
                    const serviceSlug = serviceSlugFor(s);
                    return <li key={i} className="flex items-center gap-3 py-3 border-b border-[#1A1A1A]/10">
                      <Check className="w-4 h-4 text-[#1B3B2B]" strokeWidth={2} />
                      {serviceSlug ? <Link to={`/servicios/${serviceSlug}`} className="text-[#1A1A1A] link-underline">{s}</Link> : <span>{s}</span>}
                    </li>;
                  })}
                </ul>
              </div>
            )}
          </Reveal>

          <aside className="col-span-12 md:col-span-5 md:pl-6">
            <div className="sticky top-32 border border-[#1A1A1A]/15 p-8 bg-[#EFEBE2]">
              <div className="eyebrow mb-4">Trabajemos juntos</div>
              <p className="font-serif text-2xl leading-tight">Diseñemos el plan de implementación adecuado para su organización.</p>
              <a href="/#contacto" className="mt-6 inline-flex kb-btn" data-testid="norma-cta-contact">
                Solicitar asesoría <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <div className="mt-6 pt-6 border-t border-[#1A1A1A]/15 text-xs text-[#4A4A4A]">
                Diagnóstico · Implementación · Auditoría interna · Preparación para certificación · Mantenimiento
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
